import React from 'react'
import { useNavigate } from 'react-router-dom'

import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { NumberString, useModalContext } from '@fourthwall/components'
import { pluralize } from '@fourthwall/utils/dist/string'
import { useGetTopKudosQuery } from '@hooks/useGetTopKudosQuery'
import { useKudosOrdersCountQuery } from '@modules/Dashboard/Home/queries/useKudosOrdersCountQuery'
import { routing } from '@utils/routing'

import { SendThankYouModal } from '../SendThankYouModal'
import { ThankYousBarView } from './ThankYousBarView'

export type ThankYousBarContainerProps = {
  type: 'REDIRECT' | 'MODAL'
  onModalSuccess?: () => void
}

export const ThankYousBarContainer = ({ type, onModalSuccess }: ThankYousBarContainerProps) => {
  const navigate = useNavigate()
  const { kudosOrdersCountQuery } = useKudosOrdersCountQuery([{ state: ['WAITING'] }])
  const { getTopKudosQuery, invalidateGetTopKudosQuery } = useGetTopKudosQuery([{ size: 1000 }])
  const { open } = useModalContext()
  const { dashboardPermissions } = useDashboardPermissions()

  const handleSendThankYouModalSuccess = () => {
    onModalSuccess?.()
    invalidateGetTopKudosQuery()
  }

  const handleActionBarClick = () => {
    if (type === 'REDIRECT') {
      navigate(routing.contributions.thankYous.self)

      return
    }

    open(SendThankYouModal, {
      thankYous: getTopKudosQuery.data?.orders!,
      onSuccess: handleSendThankYouModalSuccess,
    })
  }

  const getTitle = () => {
    const topSupportersCount = getTopKudosQuery.data?.page.elementsTotal

    if (!topSupportersCount) {
      return <>Thank your supporters</>
    }

    if (type === 'REDIRECT') {
      return (
        <>
          Thank <NumberString value={topSupportersCount} /> top{' '}
          {pluralize(topSupportersCount, 'supporter')}
        </>
      )
    }

    if (type === 'MODAL') {
      return (
        <>
          Start thanking top <NumberString value={topSupportersCount} />{' '}
          {pluralize(topSupportersCount, 'supporter')}
        </>
      )
    }
  }

  if (!dashboardPermissions.contributions.thankYous) {
    return null
  }

  if ((type === 'MODAL' && !getTopKudosQuery.data?.orders) || !kudosOrdersCountQuery.data?.count) {
    return null
  }

  return <ThankYousBarView title={getTitle()} onClick={handleActionBarClick} />
}
